import React, { useState, useEffect, useMemo } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale,
  BarElement, Title
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

import { api } from '../api/reportingApi';
import { 
    InspectionTable, 
    InspectionDetailsModal, 
    ImageModal 
} from './InspectionComponents';

/**
 * DefectTypeModal Component - Shows defect type details and related inspections
 */
const DefectTypeModal = ({ 
    defectType, 
    defectData,
    statistics,
    inspections,
    loadingInspections,
    onClose,
    onViewInspection,
    onImageClick,
    onSetGroundTruth,
    addNotification,
    filters
}) => {
    if (!defectType || !defectData) return null;

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Defect Type: {defectType}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    
                    <div className="modal-body">
                        {/* Statistics Section */}
                        <div className="card mb-3">
                            <div className="card-header">
                                <h6 className="mb-0">Statistics</h6>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <ul className="list-unstyled">
                                            <li className="mb-2">
                                                <strong>Total Occurrences:</strong> 
                                                <span className="badge bg-primary ms-2">{defectData.count}</span>
                                            </li>
                                            <li className="mb-2">
                                                <strong>Unique Inspections:</strong> 
                                                <span className="badge bg-info ms-2">{defectData.inspectionCount}</span>
                                            </li>
                                            <li className="mb-2">
                                                <strong>Average Confidence:</strong> 
                                                <span className={`badge ms-2 ${
                                                    defectData.avgConfidence > 0.8 ? 'bg-success' : 
                                                    defectData.avgConfidence > 0.6 ? 'bg-warning' : 'bg-danger'
                                                }`}>
                                                    {(defectData.avgConfidence * 100).toFixed(1)}%
                                                </span>
                                            </li>
                                            <li className="mb-2">
                                                <strong>Percentage of Total:</strong> 
                                                <span className="badge bg-secondary ms-2">
                                                    {((defectData.count / statistics.totalDefects) * 100).toFixed(1)}%
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="col-md-6">
                                        <ul className="list-unstyled">
                                            <li className="mb-2">
                                                <strong>Severity:</strong> {
                                                    defectData.count > statistics.totalDefects * 0.3 ? 
                                                        <span className="badge bg-danger ms-2">High</span> :
                                                    defectData.count > statistics.totalDefects * 0.1 ? 
                                                        <span className="badge bg-warning ms-2">Medium</span> : 
                                                        <span className="badge bg-success ms-2">Low</span>
                                                }
                                            </li>
                                            <li className="mb-2">
                                                <strong>Detection Quality:</strong> {
                                                    defectData.avgConfidence > 0.8 ? 
                                                        <span className="badge bg-success ms-2">Excellent</span> :
                                                    defectData.avgConfidence > 0.6 ? 
                                                        <span className="badge bg-info ms-2">Good</span> : 
                                                        <span className="badge bg-warning ms-2">Needs Review</span>
                                                }
                                            </li>
                                            <li className="mb-2">
                                                <strong>Model Coverage:</strong> 
                                                <span className="badge bg-secondary ms-2">
                                                    {defectData.models.length > 1 ? 'Multi-model' : 'Single model'}
                                                </span>
                                            </li>
                                            <li className="mb-2">
                                                <strong>Detection Models:</strong>
                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                    {defectData.models.map(model => (
                                                        <span key={model} className="badge bg-secondary">
                                                            {model}
                                                        </span>
                                                    ))}
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inspections Section */}
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">
                                    Inspections with "{defectType}" 
                                    {!loadingInspections && ` (${inspections.length})`}
                                </h6>
                            </div>
                            <div className="card-body p-0">
                                {loadingInspections ? (
                                    <div className="text-center p-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading inspections...</span>
                                        </div>
                                        <p className="mt-2">Loading inspections...</p>
                                    </div>
                                ) : (
                                    <InspectionTable
                                        inspections={inspections}
                                        onViewDetails={onViewInspection}
                                        onImageClick={onImageClick}
                                        onSetGroundTruth={onSetGroundTruth}
                                        isLoading={loadingInspections}
                                        addNotification={addNotification}
                                        filters={{ ...filters, defectType }}
                                        compactMode={true}
                                        showTitle={false}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Focused DefectTypeAnalysis Component
 * Purpose: Analyze and visualize defect types distribution and confidence
 */
export const DefectTypeAnalysis = ({ 
    filters,
    title = "Defect Type Distribution",
    showCharts = true,
    showTable = true,
    compactMode = false,
    onDefectTypeClick = null,
    addNotification = null,
    modelList = []
}) => {
    const [selectedDefectType, setSelectedDefectType] = useState(null);
    const [chartType, setChartType] = useState('doughnut');
    
    // Backend state
    const [analysisData, setAnalysisData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for inspection details
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [filteredInspections, setFilteredInspections] = useState([]);
    const [loadingInspections, setLoadingInspections] = useState(false);
    const [showDefectModal, setShowDefectModal] = useState(false);

    console.log('[DefectTypeAnalysis] Component rendered with filters:', filters);

    // Function to filter out unwanted defect types
    const filterDefectTypes = (defectTypes) => {
        const unwantedTypes = [
            'uncategorized defect',
            'uncategorized',
            'unknown',
            'defective',
            'good',
            'normal',
            'background'
        ];
        
        return defectTypes.filter(defectType => 
            !unwantedTypes.some(unwanted => 
                defectType.type.toLowerCase().includes(unwanted.toLowerCase())
            )
        );
    };

    // Fetch data from backend
    useEffect(() => {
        const fetchAnalysisData = async () => {
            if (!filters) {
                console.log('[DefectTypeAnalysis] No filters provided, skipping fetch');
                return;
            }

            console.log('[DefectTypeAnalysis] Fetching analysis data with filters:', filters);
            setIsLoading(true);
            setError(null);
            
            try {
                const result = await api.getDefectTypeAnalysis(filters);
                console.log('[DefectTypeAnalysis] Raw analysis data received:', result);
                
                // Filter out unwanted defect types
                if (result && result.defect_types) {
                    result.defect_types = filterDefectTypes(result.defect_types);
                    console.log('[DefectTypeAnalysis] Filtered defect types:', result.defect_types);
                }
                
                setAnalysisData(result);
            } catch (err) {
                console.error("[DefectTypeAnalysis] Error fetching defect analysis:", err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAnalysisData();
    }, [JSON.stringify(filters)]);

    // Fetch inspections for a specific defect type
    const fetchInspectionsForDefectType = async (defectType) => {
        console.log('[DefectTypeAnalysis] Fetching inspections for defect type:', defectType);
        setLoadingInspections(true);
        
        try {
            // Create filters for this specific defect type
            const defectFilters = {
                ...filters,
                defectType: defectType
            };
            
            console.log('[DefectTypeAnalysis] Using filters for inspection fetch:', defectFilters);
            
            // Assuming there's an API method to get inspections by defect type
            const inspections = await api.getInspectionsByDefectType(defectFilters);
            console.log('[DefectTypeAnalysis] Fetched inspections:', inspections);
            
            setFilteredInspections(inspections || []);
        } catch (err) {
            console.error("[DefectTypeAnalysis] Error fetching inspections for defect type:", err);
            if (addNotification) {
                addNotification({
                    type: 'danger',
                    message: `Failed to load inspections for ${defectType}: ${err.message}`
                });
            }
            setFilteredInspections([]);
        } finally {
            setLoadingInspections(false);
        }
    };

    // Process chart data
    const chartData = useMemo(() => {
        if (!analysisData) {
            console.log('[DefectTypeAnalysis] No analysis data for chart');
            return null;
        }
        
        const { defect_types } = analysisData;
        console.log('[DefectTypeAnalysis] Processing chart data for defect types:', defect_types);
        
        const top10Defects = defect_types.slice(0, 10);
        
        const labels = top10Defects.map(d => d.type);
        const counts = top10Defects.map(d => d.count);
        const avgConfidences = top10Defects.map(d => d.avgConfidence * 100);

        const colors = [
            '#dc3545', '#ff6384', '#ff9f40', '#ffcd56', '#4bc0c0',
            '#36a2eb', '#9966ff', '#c9cbcf', '#ff6b6b', '#4ecdc4'
        ];
        
        return {
            distribution: {
                labels,
                datasets: [{
                    label: 'Defect Count',
                    data: counts,
                    backgroundColor: colors.slice(0, labels.length),
                    borderColor: colors.slice(0, labels.length).map(c => c + 'dd'),
                    borderWidth: 2
                }]
            },
            confidence: {
                labels,
                datasets: [{
                    label: 'Average Confidence (%)',
                    data: avgConfidences,
                    backgroundColor: 'rgba(54, 162, 235, 0.5)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 2
                }]
            }
        };
    }, [analysisData]);

    // Basic statistics
    const statistics = useMemo(() => {
        if (!analysisData) {
            console.log('[DefectTypeAnalysis] No analysis data for statistics');
            return null;
        }
        
        const { statistics, defect_types } = analysisData;
        const mostCommon = defect_types.length > 0 ? defect_types[0] : { type: 'None', count: 0 };
        const defectRate = statistics.totalInspections > 0
            ? (statistics.defectiveInspections / statistics.totalInspections * 100)
            : 0;
            
        return {
            totalDefects: statistics.totalDefects,
            uniqueTypes: statistics.uniqueTypes,
            mostCommon,
            defectRate,
            inspectionCount: statistics.totalInspections
        };
    }, [analysisData]);

    const handleDefectTypeClick = async (defectType) => {
        console.log('[DefectTypeAnalysis] Defect type clicked:', defectType);
        setSelectedDefectType(defectType);
        setShowDefectModal(true);
        
        // Fetch inspections for this defect type
        await fetchInspectionsForDefectType(defectType);
        
        if (onDefectTypeClick) {
            onDefectTypeClick(defectType);
        }
    };

    const handleCloseDefectModal = () => {
        setShowDefectModal(false);
        setSelectedDefectType(null);
        setFilteredInspections([]);
    };

    const handleSetGroundTruth = async (inspectionId, status) => {
        console.log('[DefectTypeAnalysis] Setting ground truth:', inspectionId, status);
        try {
            await api.setGroundTruth(inspectionId, status);
            
            // Update the inspection in our local state
            setFilteredInspections(prev => 
                prev.map(inspection => 
                    inspection._id === inspectionId 
                        ? { ...inspection, confirmation_status: status, confirmed_by: 'user' }
                        : inspection
                )
            );
            
            if (addNotification) {
                addNotification({
                    type: 'success',
                    message: `Ground truth set to ${status}`
                });
            }
        } catch (err) {
            console.error('[DefectTypeAnalysis] Error setting ground truth:', err);
            if (addNotification) {
                addNotification({
                    type: 'danger',
                    message: `Failed to set ground truth: ${err.message}`
                });
            }
        }
    };

    // Loading states
    if (isLoading) {
        return (
            <div className="card">
                <div className="card-body text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Analyzing Defect Types...</span>
                    </div>
                    <p className="mt-2">Processing defect data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body text-center text-danger p-4">
                    <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
                    <h6>Error Loading Analysis</h6>
                    <p className="small">{error}</p>
                </div>
            </div>
        );
    }

    // if (!analysisData || !statistics || statistics.totalDefects === 0) {
    console.log("Defect_types:", analysisData.defect_types, "defectTypes:", analysisData.defectTypes)
    if (!analysisData || statistics.totalDefects === 0 || analysisData.defect_types.length === 0) {
        return (
            <div className="card">
                <div className="card-body text-center text-muted p-4">
                    <i className="fas fa-chart-pie fa-3x mb-3"></i>
                    <h6>No Defect Types Available</h6>
                    <p className="small">No defect types were found for the selected filters.</p>
                </div>
            </div>
        );
    }

    const selectedDefectData = selectedDefectType 
        ? analysisData.defect_types.find(d => d.type === selectedDefectType)
        : null;

    return (
        <div className={`defect-type-analysis ${compactMode ? 'compact' : ''}`}>
            {/* Header with Key Stats */}
            <div className="card mb-3">
                <div className="card-header bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-chart-pie me-2"></i>
                            {title}
                        </h5>
                        {showCharts && (
                            <div className="btn-group btn-group-sm" role="group">
                                <button 
                                    className={`btn btn-outline-light ${chartType === 'doughnut' ? 'active' : ''}`}
                                    onClick={() => setChartType('doughnut')}
                                    title="Pie Chart"
                                >
                                    <i className="fas fa-chart-pie"></i>
                                </button>
                                <button 
                                    className={`btn btn-outline-light ${chartType === 'bar' ? 'active' : ''}`}
                                    onClick={() => setChartType('bar')}
                                    title="Bar Chart"
                                >
                                    <i className="fas fa-chart-bar"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Key Statistics */}
                <div className="card-body">
                    <div className="row g-3 mb-3">
                        <div className="col-md-3 col-6">
                            <div className="text-center">
                                <h4 className="text-danger mb-0">{statistics.totalDefects}</h4>
                                <small className="text-muted">Total Defects</small>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="text-center">
                                {/* <h4 className="text-warning mb-0">{statistics.uniqueTypes}</h4> */}
                                <h4 className="text-warning mb-0">{analysisData.defect_types.length}</h4>
                                <small className="text-muted">Defect Types</small>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="text-center">
                                <h4 className="text-info mb-0">{statistics.defectRate.toFixed(1)}%</h4>
                                <small className="text-muted">Defect Rate</small>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="text-center">
                                <h4 className="text-success mb-0">{statistics.inspectionCount}</h4>
                                <small className="text-muted">Inspections</small>
                            </div>
                        </div>
                    </div>
                    
                    {/* Most Common Defect */}
                    {statistics.mostCommon.count > 0 && (
                        <div className="alert alert-warning mb-0">
                            <i className="fas fa-info-circle me-2"></i>
                            Most common defect: <strong>{statistics.mostCommon.type}</strong> 
                            ({statistics.mostCommon.count} occurrences, 
                            {(statistics.mostCommon.count / statistics.totalDefects * 100).toFixed(1)}% of all defects)
                        </div>
                    )}
                </div>
            </div>

            {/* Charts Section */}
            {showCharts && chartData && (
                <div className="row mb-3">
                    <div className="col-lg-6">
                        <div className="card h-100">
                            <div className="card-header">
                                <h6 className="mb-0">Defect Type Distribution</h6>
                            </div>
                            <div className="card-body">
                                <div style={{ height: compactMode ? '250px' : '300px' }}>
                                    {chartType === 'doughnut' ? (
                                        <Doughnut 
                                            data={chartData.distribution}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { 
                                                        position: 'right',
                                                        labels: { boxWidth: 12, font: { size: 11 } }
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: (context) => {
                                                                const label = context.label || '';
                                                                const value = context.parsed || 0;
                                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                                const percentage = ((value / total) * 100).toFixed(1);
                                                                return `${label}: ${value} (${percentage}%)`;
                                                            }
                                                        }
                                                    }
                                                },
                                                onClick: (event, elements) => {
                                                    if (elements.length > 0) {
                                                        const index = elements[0].index;
                                                        const defectType = chartData.distribution.labels[index];
                                                        handleDefectTypeClick(defectType);
                                                    }
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Bar 
                                            data={chartData.distribution}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: { legend: { display: false } },
                                                scales: {
                                                    y: { 
                                                        beginAtZero: true,
                                                        title: { display: true, text: 'Count' }
                                                    },
                                                    x: { 
                                                        ticks: { maxRotation: 45, minRotation: 45 }
                                                    }
                                                },
                                                onClick: (event, elements) => {
                                                    if (elements.length > 0) {
                                                        const index = elements[0].index;
                                                        const defectType = chartData.distribution.labels[index];
                                                        handleDefectTypeClick(defectType);
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="col-lg-6">
                        <div className="card h-100">
                            <div className="card-header">
                                <h6 className="mb-0">Detection Confidence</h6>
                            </div>
                            <div className="card-body">
                                <div style={{ height: compactMode ? '250px' : '300px' }}>
                                    <Bar 
                                        data={chartData.confidence}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                y: { 
                                                    beginAtZero: true, 
                                                    max: 100,
                                                    title: { display: true, text: 'Confidence (%)' }
                                                },
                                                x: { 
                                                    ticks: { maxRotation: 45, minRotation: 45 }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Defect Types Table */}
            {showTable && (
                <div className="card">
                    <div className="card-header">
                        <h6 className="mb-0">Defect Type Details</h6>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Defect Type</th>
                                        <th>Count</th>
                                        <th>Percentage</th>
                                        <th>Avg Confidence</th>
                                        <th>Detection Models</th>
                                        <th>Inspections</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analysisData.defect_types.map((defect) => {
                                        const percentage = statistics.totalDefects > 0 
                                            ? (defect.count / statistics.totalDefects * 100) 
                                            : 0;
                                        return (
                                            <tr 
                                                key={defect.type} 
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleDefectTypeClick(defect.type)}
                                            >
                                                <td><strong>{defect.type}</strong></td>
                                                <td>
                                                    <span className="badge bg-primary rounded-pill">
                                                        {defect.count}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="progress" style={{ height: '20px' }}>
                                                        <div 
                                                            className="progress-bar bg-warning text-dark"
                                                            style={{ width: `${percentage}%` }}
                                                        >
                                                            {percentage.toFixed(1)}%
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${
                                                        defect.avgConfidence > 0.8 ? 'bg-success' : 
                                                        defect.avgConfidence > 0.6 ? 'bg-warning' : 'bg-danger'
                                                    }`}>
                                                        {(defect.avgConfidence * 100).toFixed(1)}%
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {defect.models.map(model => (
                                                            <span key={model} className="badge bg-secondary fw-normal">
                                                                {model}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="badge bg-info">
                                                        {defect.inspectionCount}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button 
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDefectTypeClick(defect.type);
                                                        }}
                                                        title="View details and inspections"
                                                    >
                                                        <i className="fas fa-search"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Defect Type Modal */}
            {showDefectModal && selectedDefectType && (
                <DefectTypeModal
                    defectType={selectedDefectType}
                    defectData={selectedDefectData}
                    statistics={statistics}
                    inspections={filteredInspections}
                    loadingInspections={loadingInspections}
                    onClose={handleCloseDefectModal}
                    onViewInspection={setSelectedInspection}
                    onImageClick={setSelectedImageUrl}
                    onSetGroundTruth={handleSetGroundTruth}
                    addNotification={addNotification}
                    filters={filters}
                />
            )}

            {/* Inspection Details Modal */}
            {selectedInspection && (
                <InspectionDetailsModal
                    inspection={selectedInspection}
                    modelList={modelList}
                    onClose={() => setSelectedInspection(null)}
                    onImageClick={setSelectedImageUrl}
                    onSetGroundTruth={handleSetGroundTruth}
                    isLoading={loadingInspections}
                    addNotification={addNotification}
                />
            )}

            {/* Image Modal */}
            {selectedImageUrl && (
                <ImageModal
                    src={selectedImageUrl}
                    alt="Inspection Image"
                    show={!!selectedImageUrl}
                    onClose={() => setSelectedImageUrl(null)}
                />
            )}
        </div>
    );
};